import { useState } from 'react'
import { Button, Checkbox, Fa, Tooltip } from '@eltoro-ui/components'
import classNames from 'classnames'
import { simplifyEnum, userFriendlyAudienceName } from 'Helpers'
import {
  AudienceRowType,
  checkIfMappedHomesIsDisabled,
} from '../AudienceSelector'
import { useAppContext } from 'Contexts'

export const AudienceRow: React.FC<{
  row: AudienceRowType
  editsDisabled?: boolean
  onChange: (
    jobToChange: AudienceRowType,
    attached: boolean,
    exclude?: boolean,
  ) => void
  audienceRows: AudienceRowType[]
  isMappedHomesDisabled?: boolean
  isExcludeDisabled?: boolean
  attachedNonExcludedCount?: number
  olCompletedOrArchived?: boolean
}> = ({
  row,
  editsDisabled,
  onChange,
  audienceRows,
  isMappedHomesDisabled,
  isExcludeDisabled,
  attachedNonExcludedCount,
  olCompletedOrArchived,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { isReadOnly } = useAppContext()

  const disableDetach =
    row.subType === 'AUDIENCE_SUB_TYPE_HOMES' && isMappedHomesDisabled

  return (
    <>
      <div
        className={classNames(
          'AudienceRow-name max-w-l flex min-h-[1.5rem] items-center gap-1 break-words py-1',
          {
            'pl-8': row.audienceId && row.subType,
          },
        )}
      >
        {row.audienceId && row.subType && (
          <Fa
            icon="arrow-turn-down-right"
            size={1}
            color="grey"
            className="pb-1 pl-1"
          />
        )}
        {row.type === 'AUDIENCE_TYPE_VR' &&
          row.status === 'AUDIENCE_STATUS_COMPLETED' &&
          row.subJobs &&
          row.subJobs.length > 0 && (
            <Button
              iconOnly={
                <Fa icon={isOpen ? 'caret-down' : 'caret-right'} size={1} />
              }
              onClick={() => setIsOpen(!isOpen)}
              className="self-center"
            />
          )}
        <div
          className={classNames('flex items-center gap-1', {
            'font-bold': !row.audienceId,
          })}
        >
          {row.locked && !row.audienceId && (
            <Fa icon="lock" size={1} className="!text-primary" />
          )}
          {row.dataProduct === 'DATA_PRODUCT_CUSTOM_AUDIENCE' && (
            <span data-tooltip="top" aria-label="This is a premium audience.">
              <Fa icon="fa-gem" size={1} type="duotone" />
            </span>
          )}
          {row.audienceId && row.subType
            ? userFriendlyAudienceName(row.subType)
            : row.name || 'No name'}
        </div>
      </div>
      <div className="AudienceRow-type">
        {row.type ? userFriendlyAudienceName(row.type) : 'n/a'}
      </div>
      <div className="AudienceRow-matched">
        {(() => {
          if (!row.quoted) return 'Quoting'
          if (
            (row.matched || 0) > 1 &&
            row.type === 'AUDIENCE_TYPE_IPSFORGEOJSON'
          )
            return 'Matched'
          return row.matched?.toLocaleString() || ''
        })()}
      </div>
      {!editsDisabled && (
        <>
          <div className="AudienceRow-attach justify-self-end">
            <Checkbox
              checked={row.attached}
              onChange={() => {
                if (row.id) onChange(row, true)
              }}
              size="m"
              disabled={isReadOnly || olCompletedOrArchived}
            />
          </div>
          <div className="AudienceRow-detach justify-self-end">
            {(() => {
              const check = (
                <Checkbox
                  checked={!row.attached}
                  onChange={() => {
                    if (row.id) onChange(row, false)
                  }}
                  size="m"
                  disabled={
                    disableDetach || isReadOnly || olCompletedOrArchived
                  }
                  className={disableDetach ? 'opacity-20' : undefined}
                />
              )
              if (disableDetach)
                return (
                  <Tooltip
                    position="left"
                    content="Must include Mapped Homes when Other Device IDs in Home count is less than 30,000."
                    classNameTooltip="w-[15rem] top-[-10px]"
                    className="focus:rounded-sm"
                  >
                    {check}
                  </Tooltip>
                )
              return check
            })()}
          </div>
          <div className="AudienceRow-exclude justify-self-end">
            {(() => {
              const check = (
                <Checkbox
                  checked={row.excluded}
                  onChange={() => {
                    if (row.id) onChange(row, true, !row.excluded)
                  }}
                  size="m"
                  disabled={
                    isExcludeDisabled || isReadOnly || olCompletedOrArchived
                  }
                />
              )
              if (isExcludeDisabled)
                return (
                  <Tooltip
                    position="left"
                    content="Unable to exclude this audience. At least one included audience is required."
                    classNameTooltip="w-[15rem] top-[-10px]"
                    className="focus:rounded-sm"
                  >
                    {check}
                  </Tooltip>
                )
              return check
            })()}
          </div>
        </>
      )}
      {((!row.audienceId && row.type !== 'AUDIENCE_TYPE_VR') ||
        !isOpen ||
        (row.type === 'AUDIENCE_TYPE_VR' &&
          row.subJobs &&
          row.subJobs.length === 0)) && (
        <div className="box border-grey-200 col-start-1 col-end-7 border-b" />
      )}
      {row.type === 'AUDIENCE_TYPE_VR' &&
        isOpen &&
        row.status === 'AUDIENCE_STATUS_COMPLETED' && (
          <>
            {row?.subJobs?.map((subJob) => {
              return (
                <div
                  className="AudienceRow__sub-jobs bg-gray animate-slidedown col-start-1 col-end-7"
                  key={subJob.id}
                >
                  <div className="AudienceRow__sub-job-row grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] items-center gap-x-4">
                    <AudienceRow
                      row={subJob}
                      editsDisabled={editsDisabled}
                      onChange={onChange}
                      audienceRows={audienceRows}
                      isMappedHomesDisabled={checkIfMappedHomesIsDisabled(row)}
                      isExcludeDisabled={
                        !subJob.excluded && (attachedNonExcludedCount ?? 0) <= 1
                      }
                      olCompletedOrArchived={olCompletedOrArchived}
                    />
                  </div>
                </div>
              )
            })}
          </>
        )}
    </>
  )
}
